import cx from 'classnames';
import type { FunctionComponent, ReactNode } from 'react';

import { TreeViewLayoutTable } from './TreeViewLayoutTable';
import { TreeViewLayoutTreeView } from './TreeViewLayoutTreeView';

import styles from './TreeViewLayout.module.less';

type Props = {
  children: ReactNode;
  className?: string;
};

type TreeViewLayoutCompoundComponent = FunctionComponent<Props> & {
  /**
   * Wrapper for your react-arborist Tree component
   * @param {Props} props - The props for the TreeViewLayoutTreeView component
   * @param {ReactNode | string} props.header - The header for the TreeViewLayoutTreeView component
   * @param {ReactNode} props.children - The children for the TreeViewLayoutTreeView component
   * @param {string} [props.className] - The className for the TreeViewLayoutTreeView component
   */
  TreeView: typeof TreeViewLayoutTreeView;
  /**
   * Wrapper for your table/data grid component
   * @param {Props} props - The props for the TreeViewLayoutTreeView component
   * @param {ReactNode | string} props.header - The header for the TreeViewLayoutTreeView component
   * @param {ReactNode} props.children - The children for the TreeViewLayoutTreeView component
   * @param {string} [props.className] - The className for the TreeViewLayoutTreeView component
   */
  Table: typeof TreeViewLayoutTable;
};

/**
 * A reusable component for displaying a tree view on the left,
 * with some kind of table data on the right. Provides all the components
 * that apply the correct CSS, but you can put whatever you want in the contents
 * and this does NOT render a react-arborist tree or anything.
 *
 * The markup looks something like this
 *
 * @example
 * <TreeViewLayout>
 *   <TreeViewLayout.TreeView header="Files">
 *     Your tree view component here
 *   </TreeViewLayout.TreeView>
 *   <TreeViewLayout.Table header={<MyTableHeader />}>
 *     Your table component here
 *   </TreeViewLayout.Table>
 * </TreeViewLayout>
 */
export const TreeViewLayout: TreeViewLayoutCompoundComponent = ({ children, className }) => (
  <div className={cx(styles.layout, className)}>
    {children}
  </div>
);

TreeViewLayout.TreeView = TreeViewLayoutTreeView;
TreeViewLayout.Table = TreeViewLayoutTable;
